<template>
  <div>
    <v-dialog
      v-model="isDialogOpen"
      max-width="720"
    >
      <template v-slot:activator="{ props }">
        <v-btn
          color="primary"
          variant="flat"
          v-bind="props"
        >
          サービスの追加
        </v-btn>
      </template>

      <v-card
        class="pa-4"
      >
        <page-title value="園提供サービス 新規作成" />
        
        <div class="my-4">
          <v-text-field
            label="サービス名"
            variant="outlined"
            v-model="serviceName"
            counter
            maxlength="30"
          />
        </div>

        <div class="my-4">
          <v-select
            label="請求条件"
            variant="outlined"
            :items="serviceTypes"
            item-value="value"
            item-title="label"
            v-model="serviceType"
          />
        </div>

        <div class="my-4">
          <v-select
            label="支払い月"
            variant="outlined"
            :items="paymentTypes"
            item-value="value"
            item-title="label"
            v-model="paymentMonthType"
          />
        </div>

        <div class="my-4">
          <v-row>
            <v-col>
              <v-text-field
                label="単価"
                variant="outlined"
                v-model.number="unitPrice"
                type="number"
              />
            </v-col>

            <v-col v-if="serviceType === 'perUse'">
              <v-text-field
                label="上限"
                variant="outlined"
                v-model.number="maxPrice"
                type="number"
              />
            </v-col>
          </v-row>
        </div>

        <div class="my-4">
          <v-select
            label="勘定項目"
            variant="outlined"
            :items="accountTypes"
            item-value="value"
            item-title="label"
            v-model="accountType"
          />
        </div>

        <div class="my-4">
          <v-progress-linear v-if="isCreating" indeterminate color="primary" />
        </div>

        <div class="d-flex">
          <v-spacer />
          <v-btn
            color="gray"
            class="mx-2"
            variant="flat"
            @click="cancel"
          >
            キャンセル
          </v-btn>
          <v-btn
            color="primary"
            class="mx-2"
            variant="flat"
            :disabled="!isInputValid || isCreating"
            @click="create"
          >
            作成
          </v-btn>
          <v-spacer />
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

import PageTitle from '@/components/atoms/PageTitle.vue';
import { schoolServiceModule } from '@/store/dataModules/schoolServiceModule';

interface DataType {
  isDialogOpen: boolean;
  isCreating: boolean;

  serviceName: string;
  serviceType: string | null;
  paymentMonthType: string | null;
  unitPrice: number | null;
  maxPrice: number | null;
  accountType: string | null;
}

export default defineComponent({
  components: {
    PageTitle,
  },

  data: (): DataType => ({
    isDialogOpen: false,
    isCreating: false,

    serviceName: '',
    serviceType: null,
    paymentMonthType: null,
    unitPrice: null,
    maxPrice: null,
    accountType: null,
  }),

  computed: {
    serviceTypes() {
      return [
        { value: 'perUse', label: '回数' },
        { value: 'useMonth', label: '利用月額' },
        { value: 'monthly', label: '固定月額' },
      ]
    },
    paymentTypes() {
      return [
        { value: '0', label: '延長料金と同月請求' },
        { value: '1', label: '延長料金の翌月請求' },
        { value: '2', label: '延長料金の翌々月請求' },
      ]
    },
    accountTypes() {
      return [
        { value: 'food', label: '主食・副食費' },
        { value: 'other', label: 'その他' },
      ]
    },
    isInputValid(): boolean {
      // ⭕️ 修正3: !this.unitPrice だと「0円」を入力した時にエラー扱いになるのを修正
      if (
        this.serviceName.trim().length === 0
        || this.unitPrice === null || this.unitPrice === undefined
        || !this.accountType
        || !this.serviceType
        || !this.paymentMonthType
      ) {
        return false;
      }
      return true;
    }
  },

  methods: {
    cancel() {
      this.isDialogOpen = false;

      // ⭕️ 修正4: accountType がクリアされていなかったバグを修正
      this.serviceName = '';
      this.serviceType = null;
      this.paymentMonthType = null;
      this.unitPrice = null;
      this.maxPrice = null;
      this.accountType = null;
    },
    async create() {
      // isInputValid 側で null チェックしているので、ここは厳密な型チェックのみに留める
      if (
        this.serviceType
        && this.unitPrice !== null
        && this.accountType
        && this.paymentMonthType
      ) {
        this.isCreating = true;

        const paymentMonthType = parseInt(this.paymentMonthType, 10);
        
        await schoolServiceModule.createSchoolService({
          uniqueServiceName: this.serviceName,
          serviceType: this.serviceType,
          paymentMonthType: paymentMonthType,
          price: this.unitPrice,
          maximumPrice: this.maxPrice || undefined,
          accountType: this.accountType,
        });
        await schoolServiceModule.listSchoolServices();
        
        this.isCreating = false;
        this.cancel();
      }
    }
  },
})
</script>s